import { RFIStatus, alphabeticalCompare, alphabeticalSort } from 'shared'
import { CIMSColumnsOptions } from '../../../../../../../../../../../components/CIMSTable/CIMSColumn/types'
import { HelpMessage } from '../../../../../../../../../../../utils/constants'
import {
  RequestForInformation,
  useRFITrackerContext,
} from '../../../../RFITrackerContext'
import { getDeepestString } from '../../../../../../../../../../../utils/getReactNodeLength'
import { useSingleUserColumn } from '../../../../../../../../../../../components/CIMSTable/common/hooks/useSingleUserColumn'
import ResponseDisciplines from './components/ResponseDisciplines'
import Status from './components/Status'
import { CIMSColumnFilterItem } from '../../../../../../../../../../../components/CIMSTable/CIMSColumn/types/filter'
import { dateFormatter } from '../../../../../../../../../../../utils/dateFormatter'

const Help = HelpMessage.RequestsForInformation.RFITracker

export function useColumns(): CIMSColumnsOptions<RequestForInformation> {
  const {
    refetch,
    edit,
    selectOptions: { organizationOptions },
    externalLinks,
  } = useRFITrackerContext()
  return {
    columns: [
      {
        title: 'Status',
        help: Help.Status,
        render: (record) => <Status {...record} />,
        align: 'center',
        width: 125,
        sort: {
          with: 'callback',
          onCompare: (a, b) => {
            const A = RFIStatus.fromIdOrFail(a.statusId)
            const B = RFIStatus.fromIdOrFail(b.statusId)
            return alphabeticalCompare(A, B, 'priority')
          },
          default: 'ascend',
          priority: 1,
        },
        filter: {
          with: 'items',
          key: 'statusId',
          items: RFIStatus.values
            .sort(alphabeticalSort('priority'))
            .map(({ id, name, description }) => ({
              text: name,
              tooltip: description,
              value: id,
            })),
        },
      },
      {
        title: 'Number',
        help: Help.Number,
        render: 'formattedNumber',
        align: 'center',
        width: 135,
        sort: {
          with: 'key',
          key: 'formattedNumber',
          default: 'descend',
          priority: 2,
        },
        filter: { type: 'search', with: 'key', key: 'formattedNumber' },
      },
      externalLinks,
      {
        title: 'Description',
        render: ({ description }) =>
          description.length > 250
            ? `${description.substring(0, 247)}...`
            : description,
        width: 400,
        help: Help.Description,
        filter: {
          type: 'search',
          with: 'callback',
          onFilter: (value, { description }) =>
            description?.toLowerCase().includes(value.toString().toLowerCase()),
        },
      },
      {
        title: 'Requesting Organization',
        help: Help.Organization,
        render: ({ organization }) => organization?.name,
        align: 'center',
        width: 250,
        sort: {
          with: 'callback',
          onCompare: (a, b) =>
            alphabeticalCompare(a.organization, b.organization, 'name'),
        },
        filter: {
          with: 'items',
          key: 'organizationId',
          items: organizationOptions.reduce<CIMSColumnFilterItem[]>(
            (items, { label, value }) => {
              if (!value) return items
              items.push({ text: getDeepestString(label), value })
              return items
            },
            []
          ),
        },
      },
      useSingleUserColumn({
        title: 'Submitted By',
        help: Help.SubmittedBy,
        user: ({ submittedBy }) => submittedBy,
      }),
      {
        title: 'Requested Respond By Date',
        help: Help.ResponseDate,
        render: ({ requestedResponseDate }) =>
          dateFormatter.simple(requestedResponseDate),
        width: 270,
        filter: { type: 'date', with: 'key', key: 'requestedResponseDate' },
        sort: {
          with: 'key',
          key: 'requestedResponseDate',
          default: 'descend',
          priority: 2,
        },
      },
      {
        title: 'Response Disciplines',
        help: Help.ResponseDisciplines,
        render: (record) => <ResponseDisciplines {...record} />,
        width: {
          withRecords: 430,
          withoutRecords: 195,
        },
      },
    ],
    prominentCustomFields: {
      disableEditWhen: ({ statusId, deleted }) => ({
        alreadyDisabled:
          statusId === RFIStatus.Closed.id
            ? {
                why: 'invalid status',
                statusName: RFIStatus.Closed.name,
              }
            : false,
        recordInactive: deleted,
      }),
      onCheckBoolean: async ({ id, description }, input) => {
        await edit({
          variables: {
            editRequestForInformationInput: {
              id,
              description,
              customFields: { booleanInputs: [input] },
            },
          },
        })
        refetch()
      },
      onCheckBooleans: async ({ id, description }, input) => {
        await edit({
          variables: {
            editRequestForInformationInput: {
              id,
              description,
              customFields: { listInputs: { checkboxes: [input] } },
            },
          },
        })
        refetch()
      },
    },
  }
}
